import { useRef, useId, useState, useContext, useEffect, createContext, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useModalRegistration } from '@/store/uiStore';
import { useFocusTrap } from '@/hooks/useFocusTrap';

// Lets Modal.Header (when rendered) supply the dialog's accessible name via
// aria-labelledby, without forcing every Modal consumer to pass a label —
// most already render Modal.Header, which becomes the name for free.
// Consumers with a custom header (not using Modal.Header) get no
// aria-labelledby here, same as before this fix, rather than a dangling
// reference to an id nothing renders.
const ModalTitleContext = createContext<{ titleId: string; registerTitle: () => void } | null>(null);

type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

const sizeClass: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-3xl',
};

interface ModalRootProps {
  children: ReactNode;
  onClose: () => void;
  size?: ModalSize;
  zIndex?: number;
  /** Extra classes applied to the modal box itself */
  className?: string;
  /** Align modal to top (e.g. search modals) */
  align?: 'center' | 'top';
}

export function Modal({
  children,
  onClose,
  size = 'sm',
  zIndex = 50,
  className = '',
  align = 'center',
}: ModalRootProps) {
  const backdropRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const [labelledBy, setLabelledBy] = useState<string | undefined>(undefined);

  useModalRegistration();
  useFocusTrap(boxRef, onClose);

  const alignClass = align === 'top' ? 'items-start pt-16' : 'items-center';

  return createPortal(
    <div
      ref={backdropRef}
      className={`fixed inset-0 flex justify-center bg-black/60 backdrop-blur-[3px] ${alignClass}`}
      style={{ zIndex }}
      onMouseDown={(e) => {
        if (e.target === backdropRef.current) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
    >
      <div
        ref={boxRef}
        tabIndex={-1}
        className={`relative w-full ${sizeClass[size]} mx-4 glass-modal rounded-2xl flex flex-col overflow-hidden animate-modal-enter outline-none ${className}`}
      >
        <ModalTitleContext.Provider value={{ titleId, registerTitle: () => setLabelledBy(titleId) }}>
          {children}
        </ModalTitleContext.Provider>
      </div>
    </div>,
    document.body,
  );
}

/* ── Sub-components ─────────────────────────────────────────────────────────── */

interface ModalHeaderProps {
  title: ReactNode;
  onClose: () => void;
}

function ModalHeader({ title, onClose }: ModalHeaderProps) {
  const ctx = useContext(ModalTitleContext);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- registerTitle is stable for the modal's lifetime
  useEffect(() => { ctx?.registerTitle(); }, [ctx?.titleId]);

  return (
    <div className="flex items-center justify-between px-4 py-3.5 border-b border-border shrink-0">
      <span id={ctx?.titleId} className="text-sm font-semibold text-modal-text">{title}</span>
      <button
        onClick={onClose}
        className="p-1.5 rounded-lg text-modal-muted hover:text-modal-text hover:bg-panel-hover transition-colors"
        aria-label="Close"
      >
        <X size={15} />
      </button>
    </div>
  );
}

function ModalBody({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`px-4 py-4 flex flex-col gap-4 overflow-y-auto ${className}`}>
      {children}
    </div>
  );
}

function ModalFooter({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`px-4 py-3 border-t border-border shrink-0 flex items-center justify-end gap-2 ${className}`}>
      {children}
    </div>
  );
}

Modal.Header = ModalHeader;
Modal.Body   = ModalBody;
Modal.Footer = ModalFooter;
